// [목적] /onboarding — 신규 가입 단계 입력 + 실 BE submit. 데스크톱 3단계 / 모바일 4단계 분기
// [이유] 2026-05-16 reauth-redesign — docs/플리오랩/gangwonfolio-web.html OnboardingScreenA(3단계, 사진 없음)
//         와 docs/플리오랩/gangwonfolio.html OnboardingScreen(4단계, 사진 포함) 을 useIsDesktop 으로 분기.
//         WC-FE-AUTH-05 실배선 유지 — 마지막 단계 완료 시 useAuth().completeOnboarding() → POST /auth/me/onboarding.
//         oauth-login Wave 5 WC-FE-ONBOARD-PATCH 흡수.
//         2026-05-30 entry-flow EF-01~EF-04 — provider 배지 동적화 + 'custom' 옵션 제거.
//         2026-06-01 SLE-FE-03 Wave B — 진입 시 GET 하이드레이트(step+snapshot 복원) + 디바운스 PATCH 동기화.
// [사이드 이팩트] 실패 시 Toast(Snackbar) + 마지막 단계 복귀.
//                 consentVersion 고정값 'v1.0-2026-01-01' (packages/types 이관 follow_up).
//                 profileImage 매핑:
//                   데스크톱(3단계 — 사진 단계 없음): 항상 { kind: 'kakao' } 기본값 주입.
//                   모바일(4단계): photo 상태 ∈ {kakao,none} → discriminatedUnion 변환. 'custom' 완전 제거(v1.0).
//                 ?provider=<kakao|naver|google> searchParam → PROVIDER_BADGE_META 배지 동적화.
//                 normalizeProvider(null|unknown) → FALLBACK_BADGE ("소셜 계정으로 가입 중").
//                 비로그인(isAuthenticated === false) 진입 시 GET/PATCH skip — D-ON-2.
//                 AuthProvider 부트 호출 금지(AD-3) — 기존 AuthProvider 부트 단계 미변경.
// [수정 시 고려사항] onboardingInputSchema (auth.client.ts) 와 입력 구조 동기화 필수.
//                   useAuth 가 AuthProvider 하위에서만 동작 — main.tsx AuthProvider 래핑 필요.
//                   AUTH_COLORS / AUTH_GRADIENTS / AUTH_BORDER 토큰 경유 — hex/px 직접 입력 0건.
//                   데스크톱은 AuthShell 형태(좌: BrandVisualPanel / 우: 폼) 로 LoginDesktopLayout 과 톤 정렬.
//                   신규 provider 추가(애플 등) 시 PROVIDER_BADGE_META 와 OAuthProvider 타입 동시 갱신.
//                   snapshot 에 저장된 consents/displayName/photo/interests 는 본 파일 상태 키 기준으로 복원.

import { Box, ButtonBase, Snackbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useIsDesktop } from '../../hooks/useIsDesktop.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useOnboardingProgressSync } from '../../hooks/useOnboardingProgressSync.js';
import { fetchOnboardingProgress } from '../../api/onboarding.client.js';
import { AuthBackground } from './AuthChrome.js';
import { BrandVisualPanel } from './BrandVisualPanel.js';
import { AUTH_BORDER, AUTH_COLORS, AUTH_GRADIENTS, AUTH_SPACING } from './tokens.js';

// ─── Provider 배지 메타 ──────────────────────────────────────────────────────

type OAuthProvider = 'kakao' | 'naver' | 'google';

function normalizeProvider(raw: string | null): OAuthProvider | null {
  if (raw === 'kakao' || raw === 'naver' || raw === 'google') return raw;
  return null;
}

interface ProviderBadgeMeta {
  label: string; // 배지 텍스트: "카카오/네이버/구글 계정으로 가입 중"
  bg: string; // 배지 배경색 (AUTH_COLORS 토큰 값)
  textColor: string; // 배지 텍스트색 (AUTH_COLORS 토큰 값)
  photoLabel: string; // 사진 옵션 텍스트: "카카오/네이버/구글 프로필 사진 사용"
}

const PROVIDER_BADGE_META: Record<OAuthProvider, ProviderBadgeMeta> = {
  kakao: {
    label: '카카오 계정으로 가입 중',
    bg: AUTH_COLORS.kakaoBg,
    textColor: AUTH_COLORS.kakaoText,
    photoLabel: '카카오 프로필 사진 사용',
  },
  naver: {
    label: '네이버 계정으로 가입 중',
    bg: AUTH_COLORS.naverBadgeBg,
    textColor: AUTH_COLORS.naverBadgeText,
    photoLabel: '네이버 프로필 사진 사용',
  },
  google: {
    label: '구글 계정으로 가입 중',
    bg: AUTH_COLORS.googleBadgeBg,
    textColor: AUTH_COLORS.googleBadgeText,
    photoLabel: 'Google 프로필 사진 사용',
  },
};

const FALLBACK_BADGE: ProviderBadgeMeta = {
  label: '소셜 계정으로 가입 중',
  bg: AUTH_COLORS.kakaoBg,
  textColor: AUTH_COLORS.kakaoText,
  photoLabel: '소셜 프로필 사진 사용',
};

// ─── 상수 ────────────────────────────────────────────────────────────────────

const CONSENT_VERSION = 'v1.0-2026-01-01';

interface AgreeItem {
  id: 'tos' | 'privacy' | 'location' | 'marketing';
  label: string;
  required: boolean;
  hint?: string;
}

// [수정: 보안 리뷰 High-1] 약관 4줄 중 필수 3줄(이용약관·개인정보·위치정보)/선택 1줄(마케팅).
// 디자인 개편에서 위치정보를 '선택'으로 바꿨으나 BE 계약(onboarding.dto.ts consents.refine: tos && privacy && location)과
// FE 스키마(api/auth.client.ts)는 위치정보를 필수로 검증하므로, 동의를 끄면 POST /auth/me/onboarding 이 400 으로 실패해
// 온보딩을 끝낼 수 없었다. 위치정보를 선택으로 바꾸려면 BE·FE 스키마·위치 기반 기능 소비 지점·Spec 을 함께 바꿔야 한다.
// (설명 문구 hint 와 44px 터치 영역 개선은 유지)
const AGREE_ITEMS: ReadonlyArray<AgreeItem> = [
  { id: 'tos', label: '서비스 이용약관', required: true },
  { id: 'privacy', label: '개인정보 수집·이용 동의', required: true },
  { id: 'location', label: '위치정보 이용 동의', required: true, hint: '주변 관광지 추천에 사용' },
  { id: 'marketing', label: '마케팅 정보 수신', required: false, hint: '이벤트·혜택 소식 받기' },
];

interface TasteOption {
  id: string;
  label: string;
  emoji: string;
  desc: string;
}

const TASTE_OPTIONS: ReadonlyArray<TasteOption> = [
  { id: 'nature', label: '자연·풍경', emoji: '🏔', desc: '산·바다·숲을 따라' },
  { id: 'food', label: '맛집·로컬', emoji: '🥘', desc: '그 지역의 맛' },
  { id: 'culture', label: '문화·예술', emoji: '🎨', desc: '박물관·전시·역사' },
  { id: 'active', label: '액티비티', emoji: '🚴', desc: '서핑·트레킹·자전거' },
  { id: 'cafe', label: '카페·뷰', emoji: '☕', desc: '분위기 좋은 공간' },
  { id: 'healing', label: '힐링·온천', emoji: '♨️', desc: '쉼이 있는 여행' },
];

// 데스크톱 3단계 타이틀
const DESKTOP_TITLES: ReadonlyArray<{ t: string; s: string }> = [
  { t: '먼저 약관에 동의해주세요', s: '안전한 서비스 이용' },
  { t: '어떻게 불러드릴까요?', s: '언제든 변경 가능' },
  { t: '어떤 여행을 좋아하세요?', s: '1개 이상 선택' },
];

// 모바일 4단계 타이틀
const MOBILE_TITLES: ReadonlyArray<{ t: string; s: string }> = [
  { t: '먼저 약관에 동의해주세요', s: '안전한 서비스 이용을 위해 필요해요' },
  { t: '어떻게 불러드릴까요?', s: '언제든 프로필에서 바꿀 수 있어요' },
  { t: '프로필 사진을 골라주세요', s: '얼굴이 보여야 더 친근하니까요' },
  { t: '어떤 여행을 좋아하세요?', s: '추천이 더 정확해져요 · 1개 이상 선택' },
];

type AgreeMap = Record<AgreeItem['id'], boolean>;
type Interest = 'nature' | 'food' | 'culture' | 'active' | 'cafe' | 'healing';

// ─── OnboardingPage ──────────────────────────────────────────────────────────

/**
 * [목적] /onboarding 진입점 — useIsDesktop 으로 OnboardingDesktop(3단계) / OnboardingMobile(4단계) 분기
 * [이유] 디자인 원본이 데스크톱과 모바일에서 단계 구성이 다름 — 각 디자인 그대로 적용.
 *         2026-06-01 SLE-FE-03 Wave B: 진입 시 GET /auth/me/onboarding/progress 하이드레이트.
 *         비로그인 시 GET skip(D-ON-2). AuthProvider 부트 미변경(AD-3).
 * [사이드 이팩트] 공유 상태(agrees/name/photo/tastes/submitting) 는 본 컴포넌트가 소유.
 *                 자식 컴포넌트는 dumb — props 로 상태/핸들러 주입.
 *                 하이드레이트 실패(네트워크/4xx/5xx) → step0 폴백 + 비차단 Snackbar.
 *                 useOnboardingProgressSync 가 이후 입력 변경을 디바운스 PATCH 로 자동 저장.
 * [수정 시 고려사항] 분기 임계값 변경 시 useIsDesktop.ts (1024px) 와 동기화.
 *                   snapshot 복원 키 목록: displayName, photo, interests, consents.tos/privacy/location/marketing.
 *                   snapshot 은 BE에서 Record<string,unknown> 으로 반환 — 필드 접근 시 타입 가드 필요.
 */
export function OnboardingPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const isDesktop = useIsDesktop();
  const { status, user, completeOnboarding } = useAuth();
  const returnTo = searchParams.get('returnTo') ?? undefined;

  const providerRaw = searchParams.get('provider');
  const resolvedProvider = normalizeProvider(providerRaw);
  const badgeMeta = resolvedProvider ? PROVIDER_BADGE_META[resolvedProvider] : FALLBACK_BADGE;

  const maxStep = isDesktop ? 2 : 3;
  const titles = isDesktop ? DESKTOP_TITLES : MOBILE_TITLES;

  /** [목적] 인증 여부 — GET/PATCH 게이트(D-ON-2). loading 중은 false 처리로 GET skip */
  const isAuthenticated = status === 'authenticated';

  /** [목적] 온보딩 완료 사용자 여부 — enabled 게이트 (완료 후 PATCH 불필요) */
  const isAlreadyOnboarded = isAuthenticated && user?.onboardedAt != null;

  // ── 상태 ────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<number>(0);
  const [name, setName] = useState('강원여행러');
  const [photo, setPhoto] = useState<'kakao' | 'none'>('kakao');
  const [tastes, setTastes] = useState<string[]>([]);
  const [agrees, setAgrees] = useState<AgreeMap>({
    tos: false,
    privacy: false,
    location: false,
    marketing: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // ── 진입 하이드레이트 (SLE-FE-03 Sub 2) ──────────────────────────────────
  /**
   * [목적] 진입 시 1회 GET → stepIndex + snapshot 복원
   * [이유] 새로고침/재진입 시 이전 입력값 유지(수용기준 AC-01)
   * [사이드 이팩트] 비로그인(isAuthenticated=false) → GET skip, step0 유지.
   *                 GET 실패 → step0 폴백 + 비차단 Snackbar.
   *                 AuthProvider 부트 호출 금지(AD-3) — 본 useEffect 는 독립적으로 진입 시만 실행.
   * [수정 시 고려사항] isAuthenticated 가 loading→authenticated 로 전환될 때 1회 실행됨.
   *                   isAlreadyOnboarded=true 이면 하이드레이트 불필요 — 완료 후 페이지 재진입은 라우터가 처리.
   */
  useEffect(() => {
    if (!isAuthenticated || isAlreadyOnboarded) return;

    let cancelled = false;

    void fetchOnboardingProgress()
      .then((data) => {
        if (cancelled) return;
        if (data === null) {
          // 저장된 진행 없음 → step0 유지
          return;
        }
        // stepIndex 복원
        setStep(data.stepIndex);

        // snapshot 복원 — 허용 키만 안전 추출
        const snap = data.snapshot;

        if (typeof snap['displayName'] === 'string') {
          setName(snap['displayName']);
        }
        if (snap['photo'] === 'kakao' || snap['photo'] === 'none') {
          setPhoto(snap['photo'] as 'kakao' | 'none');
        }
        if (Array.isArray(snap['interests'])) {
          setTastes(snap['interests'] as string[]);
        }
        if (snap['consents'] != null && typeof snap['consents'] === 'object') {
          const c = snap['consents'] as Record<string, unknown>;
          setAgrees({
            tos: typeof c['tos'] === 'boolean' ? c['tos'] : false,
            privacy: typeof c['privacy'] === 'boolean' ? c['privacy'] : false,
            location: typeof c['location'] === 'boolean' ? c['location'] : false,
            marketing: typeof c['marketing'] === 'boolean' ? c['marketing'] : false,
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // 비차단 폴백 — step0 유지, Snackbar 알림
        setToastMessage('이전 진행 상태를 불러오지 못했습니다. 처음부터 시작합니다.');
        setToastOpen(true);
      });

    return () => {
      cancelled = true;
    };
    // isAuthenticated 가 loading→authenticated 로 전환될 때 1회 실행됨
    // isAlreadyOnboarded 포함 — 완료 전환 시 불필요한 재실행 방지
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAlreadyOnboarded]);

  // ── 디바운스 PATCH 동기화 (SLE-FE-03 Sub 3 통합) ─────────────────────────
  /**
   * [목적] snapshot 은 현재 입력값을 직렬화 — useOnboardingProgressSync 에 전달
   * [이유] 훅이 snapshot 객체 동일성으로 변경 감지 → 매 렌더마다 새 객체 생성하면 불필요한 전송 발생.
   *         useMemo 없이 일반 객체 선언 — 훅 내부 JSON.stringify 비교로 중복 전송 방지.
   * [사이드 이팩트] photo 는 snapshot 저장에 'photo' 키로, interests 는 'interests' 키로 저장.
   * [수정 시 고려사항] 새 입력 필드 추가 시 snapshot 객체에 해당 키 추가 + 하이드레이트 복원 로직 동기화.
   */
  const syncSnapshot: Record<string, unknown> = {
    displayName: name,
    photo,
    interests: tastes,
    consents: {
      tos: agrees.tos,
      privacy: agrees.privacy,
      location: agrees.location,
      marketing: agrees.marketing,
    },
  };

  const { lastError: syncError } = useOnboardingProgressSync({
    enabled: isAuthenticated && !isAlreadyOnboarded,
    stepIndex: step,
    snapshot: syncSnapshot,
  });

  // [목적] 디바운스 PATCH 재시도 소진(lastError) 시 기존 toast 채널로 비차단 안내
  // [이유] security-scan Wave B F-1 — 'gangwon:snackbar' CustomEvent 수신처 부재 갭을 OnboardingPage 자체 toast 로 보강
  // [사이드 이팩트] lastError 변화 시 1회 toast — 사용자 흐름 차단 X, 디자인 토큰/접근성 기존 Snackbar 그대로 사용
  // [수정 시 고려사항] 글로벌 'gangwon:snackbar' 리스너 도입 시 본 useEffect 중복 가능 — 그 시점에 한쪽만 유지
  useEffect(() => {
    if (syncError) {
      setToastMessage('진행 자동 저장에 실패했어요. 잠시 후 다시 시도합니다.');
      setToastOpen(true);
    }
  }, [syncError]);

  // ── 파생 ────────────────────────────────────────────────────────────────
  const requiredAgreed = AGREE_ITEMS.filter((i) => i.required).every((i) => agrees[i.id]);
  const allAgreed = AGREE_ITEMS.every((i) => agrees[i.id]);
  // [목적] 현재 step 의 타이틀 — 인덱스 범위 밖 접근 시 빈 값 폴백
  // [이유] tsconfig noUncheckedIndexedAccess=true 환경에서 titles[step] 가 undefined 가능
  // [사이드 이팩트] step 이 maxStep 초과 시(이론상 미발생) 빈 카피 렌더
  // [수정 시 고려사항] 단계 추가 시 DESKTOP_TITLES / MOBILE_TITLES 도 동시에 확장
  const currentTitle = titles[step] ?? { t: '', s: '' };

  // 단계별 진행 가능 여부 — 데스크톱(3단계)과 모바일(4단계) 의 마지막 단계 인덱스 차이를 흡수
  const canNext = (() => {
    if (step === 0) return requiredAgreed;
    if (step === 1) return name.trim().length > 0;
    if (!isDesktop && step === 2) return true; // 모바일 사진 단계
    // 마지막 단계(취향) — 데스크톱 step=2, 모바일 step=3
    return tastes.length >= 1;
  })();

  // ── 핸들러 ──────────────────────────────────────────────────────────────
  const toggleOne = (id: AgreeItem['id']): void => setAgrees((a) => ({ ...a, [id]: !a[id] }));
  const toggleAll = (): void => {
    const next = !allAgreed;
    setAgrees({ tos: next, privacy: next, location: next, marketing: next });
  };
  const toggleTaste = (id: string): void =>
    setTastes((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));

  /**
   * [목적] 단계 진행 또는 마지막 단계에서 BE 온보딩 submit
   * [이유] WC-FE-AUTH-05 — step<maxStep 은 다음 단계, step===maxStep 은 completeOnboarding 호출
   * [사이드 이팩트] 실패 시 Toast + 마지막 단계 복귀. 성공 시 useAuth 가 refetchMe + navigate
   * [수정 시 고려사항] 데스크톱은 사진 단계가 없으므로 profileImage 기본값 { kind:'kakao' } 주입
   */
  const next = (): void => {
    if (step < maxStep) {
      setStep(step + 1);
      return;
    }
    setSubmitting(true);

    // 데스크톱: 사진 단계 없음 → kakao 기본. 모바일: photo 상태 ∈ {kakao,none} (custom 제거 — v1.0)
    const profileImageValue =
      isDesktop || photo === 'kakao' ? ({ kind: 'kakao' } as const) : ({ kind: 'none' } as const);

    void completeOnboarding(
      {
        displayName: name.trim(),
        profileImage: profileImageValue,
        interests: tastes as Interest[],
        consents: {
          tos: agrees.tos,
          privacy: agrees.privacy,
          location: agrees.location,
          marketing: agrees.marketing,
        },
        consentVersion: CONSENT_VERSION,
      },
      returnTo,
    )
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : '온보딩 저장 중 오류가 발생했습니다. 다시 시도해주세요.';
        setToastMessage(message);
        setToastOpen(true);
        setStep(maxStep);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const back = (): void => {
    if (step > 0) setStep(step - 1);
  };

  const shared: SharedProps = {
    step,
    maxStep,
    currentTitle,
    name,
    photo,
    tastes,
    agrees,
    requiredAgreed,
    allAgreed,
    canNext,
    submitting,
    badgeMeta,
    setName,
    setPhoto,
    setStep,
    toggleOne,
    toggleAll,
    toggleTaste,
    next,
    back,
  };

  return (
    <>
      {isDesktop ? <OnboardingDesktop {...shared} /> : <OnboardingMobile {...shared} />}
      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}

// ─── SharedProps ─────────────────────────────────────────────────────────────

interface SharedProps {
  step: number;
  maxStep: number;
  currentTitle: { t: string; s: string };
  name: string;
  photo: 'kakao' | 'none';
  tastes: string[];
  agrees: AgreeMap;
  requiredAgreed: boolean;
  allAgreed: boolean;
  canNext: boolean;
  submitting: boolean;
  badgeMeta: ProviderBadgeMeta;
  setName: (v: string) => void;
  setPhoto: (v: 'kakao' | 'none') => void;
  setStep: (v: number) => void;
  toggleOne: (id: AgreeItem['id']) => void;
  toggleAll: () => void;
  toggleTaste: (id: string) => void;
  next: () => void;
  back: () => void;
}

// ─── OnboardingDesktop (3단계 — 약관/닉네임/취향) ────────────────────────────

/**
 * [목적] 데스크톱 3단계 온보딩 — docs/플리오랩/gangwonfolio-web.html OnboardingScreenA 1:1 포팅
 * [이유] AuthShell 구조 — 좌 50% BrandVisualPanel + 우 50% 폼 (LoginDesktopLayout 톤 정렬)
 * [사이드 이팩트] 사진 단계 없음. progress bar 3 segment. CTA gradient 블루
 * [수정 시 고려사항] 모바일 4단계와 상태 공유 — props 로 받음. 본인 상태 보유 금지
 */
function OnboardingDesktop({
  step,
  currentTitle,
  name,
  tastes,
  agrees,
  canNext,
  submitting,
  badgeMeta,
  setName,
  toggleOne,
  toggleTaste,
  next,
}: SharedProps): JSX.Element {
  return (
    <Box
      component="main"
      aria-label="강원캔버스 온보딩"
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        minHeight: '100vh',
        width: '100%',
        bgcolor: AUTH_COLORS.surface,
      }}
    >
      <BrandVisualPanel />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: AUTH_SPACING.desktopPanelPx,
          py: '48px',
          overflowY: 'auto',
          bgcolor: AUTH_COLORS.surface0,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: AUTH_SPACING.desktopCardMaxWidth }}>
          {/* Progress bar — 3 segment */}
          <Box sx={{ display: 'flex', gap: '6px', mb: '28px' }}>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: 5,
                  borderRadius: '3px',
                  bgcolor: i <= step ? AUTH_COLORS.brandBlue : AUTH_COLORS.dividerBase,
                  transition: 'all 200ms',
                }}
              />
            ))}
          </Box>

          {/* 소셜 가입 중 배지 — provider에 따라 동적 표시 */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              bgcolor: badgeMeta.bg,
              borderRadius: '99px',
              px: '11px',
              py: '5px',
              mb: '16px',
            }}
          >
            <Box component="span" aria-hidden="true" sx={{ fontSize: 11 }}>
              💬
            </Box>
            <Typography
              component="span"
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                color: badgeMeta.textColor,
                letterSpacing: '-0.1px',
              }}
            >
              {badgeMeta.label}
            </Typography>
          </Box>

          {/* 타이틀 + 부카피 */}
          <Typography
            component="h2"
            sx={{
              fontSize: 24,
              fontWeight: 800,
              color: AUTH_COLORS.textHigh,
              letterSpacing: '-0.5px',
              mb: AUTH_SPACING.headlineToSubMb,
              lineHeight: 1.3,
            }}
          >
            {currentTitle.t}
          </Typography>
          <Typography sx={{ fontSize: 13, color: AUTH_COLORS.textMid, mb: '30px' }}>
            {currentTitle.s}
          </Typography>

          {/* 본문 — 단계별 */}
          {step === 0 && <AgreeListBlock agrees={agrees} toggleOne={toggleOne} />}
          {step === 1 && <NameInputBlock name={name} setName={setName} />}
          {step === 2 && <TasteGridBlock tastes={tastes} toggleTaste={toggleTaste} />}

          {/* CTA */}
          <ButtonBase
            onClick={next}
            disabled={!canNext || submitting}
            aria-label={
              step === 2 ? '강원캔버스 시작하기' : step === 0 ? '동의하고 계속하기' : '다음 단계'
            }
            sx={{
              mt: '24px',
              width: '100%',
              background:
                canNext && !submitting ? AUTH_GRADIENTS.primaryCta : AUTH_COLORS.disabledBg,
              color: AUTH_COLORS.surface0,
              border: 'none',
              borderRadius: AUTH_BORDER.ctaRadius,
              p: '14px',
              fontSize: 14,
              fontWeight: 700,
              cursor: canNext && !submitting ? 'pointer' : 'not-allowed',
              boxShadow: canNext && !submitting ? `0 6px 16px ${AUTH_COLORS.brandShadow}` : 'none',
              letterSpacing: '-0.1px',
              '&:focus-visible': {
                outline: `${AUTH_BORDER.focusRingWidth} solid ${AUTH_COLORS.brandBlue}`,
                outlineOffset: AUTH_BORDER.focusRingOffset,
              },
            }}
          >
            {submitting
              ? '저장 중...'
              : step === 2
                ? '강원캔버스 시작하기'
                : step === 0
                  ? '동의하고 계속하기'
                  : '다음'}
          </ButtonBase>
        </Box>
      </Box>
    </Box>
  );
}

// ─── 데스크톱 본문 블록 ──────────────────────────────────────────────────────

/**
 * [목적] 데스크톱 약관 동의 리스트 (개별 4행 체크박스, 전체 동의 없음 — 디자인 원본 그대로)
 * [이유] 디자인 원본에 전체 동의 행은 모바일에만 존재
 * [사이드 이팩트] 행 클릭 시 toggleOne 호출 — label native checkbox + 행 전체 hit-area
 * [수정 시 고려사항] 각 행 색은 선택 시 brandBlue alpha 6%, 비선택은 surface0
 */
function AgreeListBlock({
  agrees,
  toggleOne,
}: {
  agrees: AgreeMap;
  toggleOne: (id: AgreeItem['id']) => void;
}): JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {AGREE_ITEMS.map((it) => (
        <Box
          key={it.id}
          component="label"
          sx={{
            display: 'flex',
            // [수정: 디자인 개편] hint 추가로 라벨 영역이 2줄이 될 수 있어 상단 정렜로 변경
            alignItems: 'flex-start',
            gap: '12px',
            p: '12px 14px',
            bgcolor: agrees[it.id] ? 'rgba(31,111,235,.06)' : AUTH_COLORS.surface0,
            border: `1.5px solid ${agrees[it.id] ? AUTH_COLORS.brandBlue : AUTH_COLORS.dividerBase}`,
            borderRadius: '11px',
            cursor: 'pointer',
            transition: 'all 150ms',
          }}
        >
          <Box
            component="input"
            type="checkbox"
            checked={!!agrees[it.id]}
            onChange={() => toggleOne(it.id)}
            sx={{ width: 18, height: 18, mt: '2px', accentColor: AUTH_COLORS.brandBlue, cursor: 'pointer', flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Typography
                component="span"
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: it.required ? AUTH_COLORS.brandBlue : AUTH_COLORS.textLow,
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  letterSpacing: '0.3px',
                }}
              >
                [{it.required ? '필수' : '선택'}]
              </Typography>
              <Typography component="span" sx={{ fontSize: 13, color: AUTH_COLORS.textHigh, fontWeight: 600 }}>
                {it.label}
              </Typography>
            </Box>
            {/* [수정: 디자인 개편] 위치정보 등 이유 설명(hint)이 모바일에만 나오고 데스크톱에는 누락되어 있었다 — 동일하게 표시한다. */}
            {it.hint && (
              <Typography sx={{ fontSize: 11, color: AUTH_COLORS.textLow, lineHeight: 1.4 }}>
                {it.hint}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/**
 * [목적] 데스크톱 닉네임 입력 (단일 input, 중앙 정렬)
 * [이유] 디자인 원본 input — border 2px brandBlue(focus) / dividerBase(blur), maxLength 20
 * [사이드 이팩트] 빈 값이면 border 회색, 값 있으면 brandBlue
 * [수정 시 고려사항] maxLength 변경 시 BE displayName.max(20) 와 동기화
 */
function NameInputBlock({
  name,
  setName,
}: {
  name: string;
  setName: (v: string) => void;
}): JSX.Element {
  return (
    <Box
      component="input"
      value={name}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
      maxLength={20}
      aria-label="닉네임"
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        border: `2px solid ${name ? AUTH_COLORS.brandBlue : AUTH_COLORS.dividerBase}`,
        borderRadius: '13px',
        p: '16px 18px',
        fontSize: 16,
        fontWeight: 600,
        outline: 'none',
        color: AUTH_COLORS.textHigh,
        bgcolor: AUTH_COLORS.surface0,
        textAlign: 'center',
        fontFamily: 'inherit',
      }}
    />
  );
}

/**
 * [목적] 취향 선택 그리드 — 2 column × 3 row, 6 옵션
 * [이유] 데스크톱·모바일 공통 형태 (디자인 원본 모두 2x3)
 * [사이드 이팩트] active 옵션 우상단 ✓ 배지. 선택 시 brandBlue alpha 6% bg + brandBlue border
 * [수정 시 고려사항] TASTE_OPTIONS 추가 시 grid 자동 확장
 */
function TasteGridBlock({
  tastes,
  toggleTaste,
}: {
  tastes: string[];
  toggleTaste: (id: string) => void;
}): JSX.Element {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '9px' }}>
      {TASTE_OPTIONS.map((t) => {
        const active = tastes.includes(t.id);
        return (
          <ButtonBase
            key={t.id}
            onClick={() => toggleTaste(t.id)}
            aria-pressed={active}
            aria-label={`${t.label} ${active ? '선택 취소' : '선택'}`}
            sx={{
              display: 'block',
              textAlign: 'left',
              bgcolor: active ? 'rgba(31,111,235,.06)' : AUTH_COLORS.surface0,
              border: `1.5px solid ${active ? AUTH_COLORS.brandBlue : AUTH_COLORS.dividerBase}`,
              borderRadius: '13px',
              p: '16px 12px',
              cursor: 'pointer',
              position: 'relative',
              transition: 'all 150ms',
              '&:focus-visible': {
                outline: `${AUTH_BORDER.focusRingWidth} solid ${AUTH_COLORS.brandBlue}`,
                outlineOffset: AUTH_BORDER.focusRingOffset,
              },
            }}
          >
            <Box
              component="span"
              aria-hidden="true"
              sx={{ fontSize: 24, display: 'block', mb: '8px' }}
            >
              {t.emoji}
            </Box>
            <Typography
              component="span"
              sx={{ fontSize: 12.5, fontWeight: 700, color: AUTH_COLORS.textHigh }}
            >
              {t.label}
            </Typography>
            {active && (
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  bgcolor: AUTH_COLORS.brandBlue,
                  color: AUTH_COLORS.surface0,
                  fontSize: 10,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✓
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// ─── OnboardingMobile (4단계 — 약관/닉네임/사진/취향) ────────────────────────

/**
 * [목적] 모바일 4단계 온보딩 — docs/플리오랩/gangwonfolio.html OnboardingScreen 1:1 포팅
 * [이유] 사진 단계 보존 — 모바일에서는 친근감 강화를 위해 프로필 사진 선택 유지
 * [사이드 이팩트] AuthBackground 글로우 위에 단일 컬럼. 상단 back/progress/skip 헤더 + 카카오 배지 + 본문 + CTA
 * [수정 시 고려사항] step 2 (사진) 만 "건너뛰기" 노출. 데스크톱과 핸들러 공유
 */
function OnboardingMobile({
  step,
  currentTitle,
  name,
  photo,
  tastes,
  agrees,
  requiredAgreed,
  allAgreed,
  canNext,
  submitting,
  badgeMeta,
  setName,
  setPhoto,
  setStep,
  toggleOne,
  toggleAll,
  toggleTaste,
  next,
  back,
}: SharedProps): JSX.Element {
  // requiredAgreed 는 step 0 표시용으로 ButtonBase aria-disabled 에 활용 가능 — 현재 canNext 로 통합
  void requiredAgreed;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        overflowY: 'auto',
        bgcolor: AUTH_COLORS.surface0,
        position: 'relative',
      }}
    >
      <AuthBackground />
      <Box
        sx={{
          position: 'relative',
          p: '18px 28px 28px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          maxWidth: AUTH_SPACING.cardMaxWidth,
          mx: 'auto',
        }}
      >
        {/* 상단: 뒤로 + 진행 점 + 건너뛰기 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '32px' }}>
          <ButtonBase
            onClick={back}
            disabled={step === 0}
            aria-label="이전 단계"
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              bgcolor: AUTH_COLORS.surface1,
              color: step === 0 ? AUTH_COLORS.textLow : AUTH_COLORS.textHigh,
              fontSize: 16,
              opacity: step === 0 ? 0.4 : 1,
            }}
          >
            ‹
          </ButtonBase>
          <Box sx={{ flex: 1, display: 'flex', gap: '5px', justifyContent: 'center' }}>
            {[0, 1, 2, 3].map((s) => (
              <Box
                key={s}
                sx={{
                  width: s === step ? 22 : 7,
                  height: 5,
                  borderRadius: '3px',
                  bgcolor: s <= step ? AUTH_COLORS.brandBlue : AUTH_COLORS.dividerBase,
                  transition: 'all 200ms',
                }}
              />
            ))}
          </Box>
          {step === 2 ? (
            <ButtonBase
              onClick={() => setStep(3)}
              aria-label="사진 단계 건너뛰기"
              sx={{
                color: AUTH_COLORS.textMid,
                fontSize: 11.5,
                fontWeight: 600,
                p: '8px 4px',
              }}
            >
              건너뛰기
            </ButtonBase>
          ) : (
            <Box sx={{ width: 34 }} aria-hidden="true" />
          )}
        </Box>

        {/* 소셜 가입 중 배지 — provider에 따라 동적 표시 */}
        <Box
          sx={{
            display: 'inline-flex',
            alignSelf: 'center',
            alignItems: 'center',
            gap: '6px',
            bgcolor: badgeMeta.bg,
            borderRadius: '99px',
            px: '11px',
            py: '5px',
            mb: '16px',
          }}
        >
          <Box component="span" aria-hidden="true" sx={{ fontSize: 11 }}>
            💬
          </Box>
          <Typography
            component="span"
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: badgeMeta.textColor,
              letterSpacing: '-0.1px',
            }}
          >
            {badgeMeta.label}
          </Typography>
        </Box>

        {/* 타이틀 */}
        <Box sx={{ textAlign: 'center', mb: '32px' }}>
          <Typography
            component="h2"
            sx={{
              fontSize: 22,
              fontWeight: 800,
              color: AUTH_COLORS.textHigh,
              letterSpacing: '-0.5px',
              mb: '7px',
              lineHeight: 1.3,
            }}
          >
            {currentTitle.t}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: AUTH_COLORS.textMid, lineHeight: 1.5 }}>
            {currentTitle.s}
          </Typography>
        </Box>

        {/* STEP 0: 약관 동의 (전체 동의 + 개별) */}
        {step === 0 && (
          <Box sx={{ mb: 'auto' }}>
            <ButtonBase
              onClick={toggleAll}
              aria-label={allAgreed ? '전체 동의 해제' : '전체 동의'}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                bgcolor: allAgreed ? 'rgba(31,111,235,.08)' : AUTH_COLORS.surface0,
                border: `2px solid ${allAgreed ? AUTH_COLORS.brandBlue : AUTH_COLORS.dividerBase}`,
                borderRadius: '14px',
                p: '16px',
                textAlign: 'left',
                mb: '16px',
                transition: 'all 150ms',
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: allAgreed ? AUTH_COLORS.brandBlue : AUTH_COLORS.surface1,
                  color: AUTH_COLORS.surface0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                ✓
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: AUTH_COLORS.textHigh, mb: '2px' }}
                >
                  전체 동의하기
                </Typography>
                <Typography sx={{ fontSize: 11, color: AUTH_COLORS.textMid }}>
                  필수 및 선택 항목 전체 동의
                </Typography>
              </Box>
            </ButtonBase>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', pl: '4px' }}>
              {AGREE_ITEMS.map((item) => (
                <Box
                  key={item.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: '11px', p: '12px' }}
                >
                  {/* [수정: 디자인 개편] 체크박스 시각 크기(22px)는 유지하되, 실제 탭 영역은 44x44px로 확장해
                      모바일 최소 터치 타겟 권장(44px) 기준을 맞춘다. */}
                  <ButtonBase
                    onClick={() => toggleOne(item.id)}
                    aria-label={`${item.label} ${agrees[item.id] ? '동의 해제' : '동의'}`}
                    sx={{
                      width: 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '6px',
                        bgcolor: agrees[item.id] ? AUTH_COLORS.brandBlue : 'transparent',
                        border: `2px solid ${agrees[item.id] ? AUTH_COLORS.brandBlue : AUTH_COLORS.dividerBase}`,
                        color: AUTH_COLORS.surface0,
                        fontSize: 12,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {agrees[item.id] ? '✓' : ''}
                    </Box>
                  </ButtonBase>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        mb: item.hint ? '2px' : 0,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: item.required ? AUTH_COLORS.brandBlue : AUTH_COLORS.textLow,
                          fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                          letterSpacing: '0.3px',
                        }}
                      >
                        [{item.required ? '필수' : '선택'}]
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ fontSize: 12.5, color: AUTH_COLORS.textHigh, fontWeight: 600 }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                    {item.hint && (
                      <Typography
                        sx={{ fontSize: 10.5, color: AUTH_COLORS.textLow, lineHeight: 1.4 }}
                      >
                        {item.hint}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* STEP 1: 닉네임 */}
        {step === 1 && (
          <Box sx={{ mb: 'auto' }}>
            <NameInputBlock name={name} setName={setName} />
            <Typography
              sx={{
                fontSize: 10.5,
                color: AUTH_COLORS.textLow,
                mt: '9px',
                textAlign: 'right',
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              }}
            >
              {name.length}/20
            </Typography>
          </Box>
        )}

        {/* STEP 2: 프로필 사진 */}
        {step === 2 && (
          <Box sx={{ mb: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: '24px' }}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: photo === 'kakao' ? AUTH_GRADIENTS.kakaoAvatar : AUTH_COLORS.surface1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 8px 20px ${AUTH_COLORS.brandShadow}`,
                  border: `4px solid ${AUTH_COLORS.surface0}`,
                  fontSize: photo === 'kakao' ? 52 : 32,
                  fontWeight: 800,
                  color: AUTH_COLORS.textLow,
                  letterSpacing: '-1px',
                }}
              >
                {photo === 'kakao' ? '🙂' : '👤'}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                {
                  id: 'kakao' as const,
                  label: badgeMeta.photoLabel,
                  desc: '기존 사진을 그대로',
                },
                { id: 'none' as const, label: '기본 이미지로', desc: '나중에 바꿔도 돼요' },
              ].map((opt) => (
                <ButtonBase
                  key={opt.id}
                  onClick={() => setPhoto(opt.id)}
                  aria-pressed={photo === opt.id}
                  aria-label={opt.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    bgcolor: photo === opt.id ? 'rgba(31,111,235,.06)' : AUTH_COLORS.surface0,
                    border: `1.5px solid ${photo === opt.id ? AUTH_COLORS.brandBlue : AUTH_COLORS.dividerBase}`,
                    borderRadius: '13px',
                    p: '13px 16px',
                    textAlign: 'left',
                    transition: 'all 150ms',
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 700, color: AUTH_COLORS.textHigh, mb: '2px' }}
                    >
                      {opt.label}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: AUTH_COLORS.textMid }}>
                      {opt.desc}
                    </Typography>
                  </Box>
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      border: `2px solid ${photo === opt.id ? AUTH_COLORS.brandBlue : AUTH_COLORS.dividerBase}`,
                      bgcolor: photo === opt.id ? AUTH_COLORS.brandBlue : AUTH_COLORS.surface0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: AUTH_COLORS.surface0,
                      fontSize: 10,
                      fontWeight: 800,
                    }}
                  >
                    {photo === opt.id ? '✓' : ''}
                  </Box>
                </ButtonBase>
              ))}
            </Box>
          </Box>
        )}

        {/* STEP 3: 여행 취향 */}
        {step === 3 && (
          <Box sx={{ mb: 'auto' }}>
            <TasteGridBlock tastes={tastes} toggleTaste={toggleTaste} />
            <Typography
              sx={{ textAlign: 'center', mt: '14px', fontSize: 11, color: AUTH_COLORS.textMid }}
            >
              <Box
                component="span"
                sx={{
                  color: tastes.length > 0 ? AUTH_COLORS.brandBlue : AUTH_COLORS.textLow,
                  fontWeight: 700,
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                }}
              >
                {tastes.length}
              </Box>
              개 선택됨
            </Typography>
          </Box>
        )}

        {/* CTA */}
        <ButtonBase
          onClick={next}
          disabled={!canNext || submitting}
          aria-label={
            step === 3 ? '강원캔버스 시작하기' : step === 0 ? '동의하고 계속하기' : '다음 단계'
          }
          sx={{
            mt: '20px',
            width: '100%',
            background: canNext && !submitting ? AUTH_GRADIENTS.primaryCta : AUTH_COLORS.disabledBg,
            color: AUTH_COLORS.surface0,
            border: 'none',
            borderRadius: AUTH_BORDER.ctaRadius,
            p: '14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: canNext && !submitting ? 'pointer' : 'not-allowed',
            boxShadow: canNext && !submitting ? `0 6px 16px ${AUTH_COLORS.brandShadow}` : 'none',
            letterSpacing: '-0.1px',
            '&:focus-visible': {
              outline: `${AUTH_BORDER.focusRingWidth} solid ${AUTH_COLORS.brandBlue}`,
              outlineOffset: AUTH_BORDER.focusRingOffset,
            },
          }}
        >
          {submitting
            ? '저장 중...'
            : step === 3
              ? '강원캔버스 시작하기'
              : step === 0
                ? '동의하고 계속하기'
                : '다음'}
        </ButtonBase>
      </Box>
    </Box>
  );
}
